import { apiService } from '../../../components/api/apiService';
import { logGovernedMcp } from './governedMcpLogger';
import type {
  AccessPolicy,
  ActivityEvent,
  GatewayApiKey,
  McpClient,
  McpEndpoint,
  PublishedCapability,
  QuotaAssignment,
  QuotaAssignmentStatus,
  QuotaScope,
  QuotaUsage,
  QuotaUser,
  ToolGroup,
} from './governedMcpTypes';

const CONNECTION = 'backend_py';
const BASE = '/api/v1/governed-mcp';

export interface GovernedMcpRemoteState {
  endpoints: McpEndpoint[];
  clients: McpClient[];
  policies: AccessPolicy[];
  published: PublishedCapability[];
  toolGroups: ToolGroup[];
  apiKeys: GatewayApiKey[];
  quotaAssignments: QuotaAssignment[];
  quotaUsages: QuotaUsage[];
  quotaUsers: QuotaUser[];
  activity: ActivityEvent[];
}

function serviceErrorMessage(err: unknown): string {
  const axiosErr = err as {
    code?: string;
    response?: { data?: { error?: { message?: string; detail?: unknown }; message?: string }; status?: number };
    message?: string;
  };
  if (axiosErr.code === 'ERR_NETWORK' || axiosErr.message === 'Network Error') {
    return 'Cannot reach BackendPYApp at http://127.0.0.1:8014. Start it with `python run.py` from BackendPYApp.';
  }
  const detail = axiosErr.response?.data?.error?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const counts = detail as { clients?: number; policies?: number; published?: number };
    if (counts.clients || counts.policies || counts.published) {
      return (
        axiosErr.response?.data?.error?.message ||
        `Cannot delete: ${counts.clients || 0} client(s), ${counts.policies || 0} policy(ies), and ${counts.published || 0} published capability(ies) still depend on this endpoint.`
      );
    }
  }
  return (
    axiosErr.response?.data?.error?.message ||
    axiosErr.response?.data?.message ||
    axiosErr.message ||
    'Governed MCP request failed'
  );
}

function asRecordList(value: unknown): Record<string, unknown>[] {
  if (Array.isArray(value)) return value.filter((item) => item && typeof item === 'object') as Record<string, unknown>[];
  return [];
}

export const governedMcpApi = {
  async getState(): Promise<GovernedMcpRemoteState> {
    logGovernedMcp('app', 'load_state');
    try {
      const data = (await apiService.get(`${BASE}/state`, {}, CONNECTION)) as Record<string, unknown>;
      const next: GovernedMcpRemoteState = {
        endpoints: asRecordList(data.endpoints) as unknown as McpEndpoint[],
        clients: asRecordList(data.clients) as unknown as McpClient[],
        policies: asRecordList(data.policies) as unknown as AccessPolicy[],
        published: asRecordList(data.published) as unknown as PublishedCapability[],
        toolGroups: asRecordList(data.toolGroups ?? data.tool_groups) as unknown as ToolGroup[],
        apiKeys: asRecordList(data.apiKeys ?? data.api_keys) as unknown as GatewayApiKey[],
        quotaAssignments: asRecordList(data.quotaAssignments ?? data.quota_assignments) as unknown as QuotaAssignment[],
        quotaUsages: asRecordList(data.quotaUsages ?? data.quota_usages) as unknown as QuotaUsage[],
        quotaUsers: asRecordList(data.quotaUsers ?? data.quota_users) as unknown as QuotaUser[],
        activity: asRecordList(data.activity) as unknown as ActivityEvent[],
      };
      logGovernedMcp('app', 'load_state_ok', {
        endpoints: next.endpoints.length,
        clients: next.clients.length,
        policies: next.policies.length,
        published: next.published.length,
        toolGroups: next.toolGroups.length,
        apiKeys: next.apiKeys.length,
        quotaAssignments: next.quotaAssignments.length,
        quotaUsages: next.quotaUsages.length,
        activity: next.activity.length,
      });
      return next;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'load_state_failed', { message });
      throw new Error(message);
    }
  },

  async upsertEndpoint(endpoint: McpEndpoint): Promise<McpEndpoint> {
    logGovernedMcp('app', 'persist_endpoint', { id: endpoint.id, slug: endpoint.slug });
    try {
      const path = `${BASE}/endpoints/${encodeURIComponent(endpoint.id)}`;
      const saved = (await apiService.put(path, endpoint, CONNECTION)) as McpEndpoint;
      logGovernedMcp('app', 'persist_endpoint_ok', { id: saved?.id || endpoint.id });
      return saved || endpoint;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'persist_endpoint_failed', { id: endpoint.id, message });
      throw new Error(message);
    }
  },

  async deleteEndpoint(id: string): Promise<void> {
    logGovernedMcp('app', 'delete_endpoint', { id });
    try {
      await apiService.delete(`${BASE}/endpoints/${encodeURIComponent(id)}`, CONNECTION);
      logGovernedMcp('app', 'delete_endpoint_ok', { id });
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'delete_endpoint_failed', { id, message });
      throw new Error(message);
    }
  },

  async upsertClient(client: McpClient): Promise<McpClient> {
    logGovernedMcp('app', 'persist_client', { id: client.id });
    try {
      const saved = (await apiService.put(
        `${BASE}/clients/${encodeURIComponent(client.id)}`,
        client,
        CONNECTION
      )) as McpClient;
      logGovernedMcp('app', 'persist_client_ok', { id: saved?.id || client.id });
      return saved || client;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'persist_client_failed', { id: client.id, message });
      throw new Error(message);
    }
  },

  async deleteClient(id: string): Promise<void> {
    logGovernedMcp('app', 'delete_client', { id });
    try {
      await apiService.delete(`${BASE}/clients/${encodeURIComponent(id)}`, CONNECTION);
      logGovernedMcp('app', 'delete_client_ok', { id });
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'delete_client_failed', { id, message });
      throw new Error(message);
    }
  },

  async upsertPolicy(policy: AccessPolicy): Promise<AccessPolicy> {
    logGovernedMcp('app', 'persist_policy', { id: policy.id });
    try {
      const saved = (await apiService.put(
        `${BASE}/policies/${encodeURIComponent(policy.id)}`,
        policy,
        CONNECTION
      )) as AccessPolicy;
      logGovernedMcp('app', 'persist_policy_ok', { id: saved?.id || policy.id });
      return saved || policy;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'persist_policy_failed', { id: policy.id, message });
      throw new Error(message);
    }
  },

  async deletePolicy(id: string): Promise<void> {
    logGovernedMcp('app', 'delete_policy', { id });
    try {
      await apiService.delete(`${BASE}/policies/${encodeURIComponent(id)}`, CONNECTION);
      logGovernedMcp('app', 'delete_policy_ok', { id });
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'delete_policy_failed', { id, message });
      throw new Error(message);
    }
  },

  async upsertPublished(item: PublishedCapability): Promise<PublishedCapability> {
    logGovernedMcp('app', 'persist_capability', { id: item.id, kind: item.capabilityKind });
    try {
      const saved = (await apiService.put(
        `${BASE}/capabilities/${encodeURIComponent(item.id)}`,
        item,
        CONNECTION
      )) as PublishedCapability;
      logGovernedMcp('app', 'persist_capability_ok', { id: saved?.id || item.id });
      return saved || item;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'persist_capability_failed', { id: item.id, message });
      throw new Error(message);
    }
  },

  async deletePublished(id: string): Promise<void> {
    logGovernedMcp('app', 'delete_capability', { id });
    try {
      await apiService.delete(`${BASE}/capabilities/${encodeURIComponent(id)}`, CONNECTION);
      logGovernedMcp('app', 'delete_capability_ok', { id });
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'delete_capability_failed', { id, message });
      throw new Error(message);
    }
  },

  async upsertToolGroup(group: ToolGroup): Promise<ToolGroup> {
    logGovernedMcp('app', 'persist_tool_group', { id: group.id });
    try {
      const saved = (await apiService.put(
        `${BASE}/tool-groups/${encodeURIComponent(group.id)}`,
        group,
        CONNECTION
      )) as ToolGroup;
      logGovernedMcp('app', 'persist_tool_group_ok', { id: saved?.id || group.id });
      return saved || group;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'persist_tool_group_failed', { id: group.id, message });
      throw new Error(message);
    }
  },

  async deleteToolGroup(id: string): Promise<void> {
    logGovernedMcp('app', 'delete_tool_group', { id });
    try {
      await apiService.delete(`${BASE}/tool-groups/${encodeURIComponent(id)}`, CONNECTION);
      logGovernedMcp('app', 'delete_tool_group_ok', { id });
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'delete_tool_group_failed', { id, message });
      throw new Error(message);
    }
  },

  async issueJwt(input: {
    userId: string;
    tenantId: string;
    email: string;
    name: string;
    roleId: string;
    isAdmin?: boolean;
    ttlSeconds: number;
  }): Promise<{ token: string; expiresAt: string; userId: string; tenantId: string }> {
    logGovernedMcp('app', 'issue_jwt', { userId: input.userId, tenantId: input.tenantId, ttlSeconds: input.ttlSeconds });
    try {
      const saved = (await apiService.post(`${BASE}/jwt-tokens`, input, CONNECTION)) as {
        token: string;
        expiresAt: string;
        userId: string;
        tenantId: string;
      };
      if (!saved?.token) {
        throw new Error('JWT was not returned');
      }
      logGovernedMcp('app', 'issue_jwt_ok', { userId: saved.userId, expiresAt: saved.expiresAt });
      return saved;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'issue_jwt_failed', { userId: input.userId, message });
      throw new Error(message);
    }
  },

  async upsertApiKey(item: GatewayApiKey): Promise<GatewayApiKey> {
    logGovernedMcp('app', 'persist_api_key', { id: item.id, kind: item.kind });
    try {
      const saved = (await apiService.put(
        `${BASE}/api-keys/${encodeURIComponent(item.id)}`,
        item,
        CONNECTION
      )) as GatewayApiKey;
      logGovernedMcp('app', 'persist_api_key_ok', { id: saved?.id || item.id });
      return saved || item;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'persist_api_key_failed', { id: item.id, message });
      throw new Error(message);
    }
  },

  async deleteApiKey(id: string): Promise<void> {
    logGovernedMcp('app', 'delete_api_key', { id });
    try {
      await apiService.delete(`${BASE}/api-keys/${encodeURIComponent(id)}`, CONNECTION);
      logGovernedMcp('app', 'delete_api_key_ok', { id });
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'delete_api_key_failed', { id, message });
      throw new Error(message);
    }
  },

  async listQuotas(): Promise<{ assignments: QuotaAssignment[]; usages: QuotaUsage[] }> {
    logGovernedMcp('app', 'list_quotas');
    try {
      const data = (await apiService.get(`${BASE}/quotas`, {}, CONNECTION)) as Record<string, unknown>;
      const bundle = {
        assignments: asRecordList(data.assignments) as unknown as QuotaAssignment[],
        usages: asRecordList(data.usages) as unknown as QuotaUsage[],
      };
      logGovernedMcp('app', 'list_quotas_ok', {
        assignments: bundle.assignments.length,
        usages: bundle.usages.length,
      });
      return bundle;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'list_quotas_failed', { message });
      throw new Error(message);
    }
  },

  async upsertQuotaAssignment(input: {
    id: string;
    scope: QuotaScope;
    scopeId: string;
    clientId: string;
    month: string;
    tokenLimit: number;
    status?: QuotaAssignmentStatus;
    name?: string;
  }): Promise<QuotaAssignment> {
    logGovernedMcp('app', 'persist_quota_assignment', {
      id: input.id,
      scope: input.scope,
      scopeId: input.scopeId,
      clientId: input.clientId,
      month: input.month,
      tokenLimit: input.tokenLimit,
      status: input.status || 'active',
    });
    try {
      const saved = (await apiService.put(
        `${BASE}/quotas/${encodeURIComponent(input.id)}`,
        {
          scope: input.scope,
          scopeId: input.scopeId,
          clientId: input.clientId,
          month: input.month,
          tokenLimit: input.tokenLimit,
          status: input.status || 'active',
          name: input.name || '',
        },
        CONNECTION
      )) as QuotaAssignment;
      logGovernedMcp('app', 'persist_quota_assignment_ok', { id: saved?.id || input.id });
      return saved;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'persist_quota_assignment_failed', { id: input.id, message });
      throw new Error(message);
    }
  },

  async recordActivity(event: ActivityEvent): Promise<ActivityEvent> {
    logGovernedMcp('app', 'persist_activity', { id: event.id, decision: event.decision });
    try {
      const saved = (await apiService.post(`${BASE}/activity`, event, CONNECTION)) as ActivityEvent;
      logGovernedMcp('app', 'persist_activity_ok', { id: saved?.id || event.id });
      return saved || event;
    } catch (err) {
      const message = serviceErrorMessage(err);
      logGovernedMcp('error', 'persist_activity_failed', { id: event.id, message });
      throw new Error(message);
    }
  },
};
